import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';

@Injectable({
  providedIn: 'root'
})
export class NativesoilTechWorksheetPdfService {

  generateTRWorksheet(worksheetRecords: any, worksheets: any[]): void {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    this.addTitlePage(
      doc,
      worksheetRecords,
      worksheets
    );

    const formattedDueDate = DateTimeUtils.getLocalDateTimeFromUTCDate(worksheetRecords.project.dueDate,'MMddyy');
    const fileNameParts = [
      worksheetRecords.project.referenceId,
      worksheetRecords.company.name,
      worksheetRecords.project.projectName,
      worksheetRecords.project.projectNumber,
      'native-soil',
      formattedDueDate,
    ]
      .filter(v => typeof v === 'string' && v.trim().length > 0)
      .map(v => this.sanitizeFileName(v));

    const fileName = fileNameParts.join('-');
    doc.save(`${fileName}.pdf`);
  }

  sanitizeFileName(value: string): string {
      return value.replace(/[\/\\:*?"<>|$%]/g, '').replace(/\s+/g, ' ').trim().replace(/\.+$/, '');
  }

  private drawHeader(
    doc: jsPDF,
    worksheetRecords: any,
    margin: number,
    usableWidth: number
  ): number {

    const formattedSampleReceivedDate = DateTimeUtils.getLocalDateTimeFromUTCDate(worksheetRecords.project.sampleReceivedDate, 'MM/dd/yyyy');
    const formattedInstructionsConfirmedAt = DateTimeUtils.getLocalDateTimeFromUTCDate(worksheetRecords.project.instructionsConfirmedAt, 'MM/dd/yyyy');
    const formattedDueDate = DateTimeUtils.getLocalDateTimeFromUTCDate(worksheetRecords.project.dueDate, 'MM/dd/yyyy');

    autoTable(doc, {
      startY: 5,
      theme: 'grid',
      margin: { left: margin, right: margin },
      tableWidth: usableWidth,

      columnStyles: {
        0: { cellWidth: usableWidth / 100 * 9 },
        1: { cellWidth: usableWidth / 100 * 18.5, fontStyle: 'bold' },
        2: { cellWidth: usableWidth / 100 * 12.5 },
        3: { cellWidth: usableWidth / 100 * 8, fontStyle: 'bold' },
        4: { cellWidth: usableWidth / 100 * 10 },
        5: { cellWidth: usableWidth / 100 * 22, fontStyle: 'bold' },
        6: { cellWidth: usableWidth / 100 * 10 },
        7: { cellWidth: usableWidth / 100 * 9, fontStyle: 'bold' }
      },

      styles: {
        fontSize: 9,
        textColor: 0,
        valign: 'middle',
        halign: 'left',
        overflow: 'linebreak',
        cellPadding: 1,
        lineColor: 0,
        lineWidth: 0.1
      },

      body: [
        [
            { content: 'Manager Notes', colSpan: 1, styles: { minCellHeight: 7 } },
            { content: '', colSpan: 5, styles: { minCellHeight: 7 } },
            { 
                content: 'Native Soil', 
                colSpan: 2,             
                styles: {
                    halign: 'center',
                    minCellHeight: 7,
                    fontStyle: 'bold',
                    fontSize: 11,
                } 
            },
        ],
        [
          'Project Name', worksheetRecords.project.projectName,
          'Sample Received Date', formattedSampleReceivedDate || '',
          'Company Name', worksheetRecords.company.name,
          'Tube Diameter', '',
        ],
        [
          'Proj. Reference', worksheetRecords.project.referenceId,
          'Instructions Confirmed', formattedInstructionsConfirmedAt || '',
          'Project Location', worksheetRecords.project.projectLocation,
          'Height', '',
        ],
        [
          'Project Number', worksheetRecords.project.projectNumber,
          'Due Date', formattedDueDate || '',
          'Oven Date', worksheetRecords.project.ovenDate || '',
          'Storage Location', worksheetRecords.project.storageCompartment || '',
        ],
        [
          { content: 'Wet Test Data', colSpan: 1, styles: { minCellHeight: 7 } },
          { content: '', colSpan: 7, styles: { minCellHeight: 7 } },
        ],
        [
          { content: 'Dry Test Data', colSpan: 1, styles: { minCellHeight: 7 } },
          { content: '', colSpan: 5, styles: { minCellHeight: 7 } },
          {
            content: worksheetRecords.project.rushTesting ? 'RUSH' : '',
            colSpan: 2,
            styles: {
              halign: 'center',
              minCellHeight: 7,
              fontStyle: 'bold',
              fontSize: 11,
            }
          }
        ]
      ]
    });

    return (doc as any).lastAutoTable.finalY;
  }

  private addTitlePage(
    doc: jsPDF,
    worksheetRecords: any,
    worksheets: any[]
  ): void {

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 5;
    const usableWidth = pageWidth - margin * 2;

    // DRAW HEADER FIRST TIME
    const headerFinalY = this.drawHeader(
      doc,
      worksheetRecords,
      margin,
      usableWidth
    );

    // START POSITION FOR MAIN TABLE
    const finalY = headerFinalY + 4;

    // WORKSHEET ROWS
    const worksheetRows: any[] = [];

    for (let i = 0; i < worksheets.length; i++) {
      const item = worksheets[i];
      const hasValue = (v: any) =>
        v !== null &&
        v !== undefined &&
        v !== '';

      const depthRange =
        hasValue(item.depth) && hasValue(item.endDepth)
          ? `${item.depth}-${item.endDepth}`
          : hasValue(item.depth)
            ? item.depth
            : hasValue(item.endDepth)
              ? item.endDepth
              : '';

      let rowSpanCount = 1;

      for (let j = i + 1; j < worksheets.length; j++) {
        if (
          worksheets[j].nativeSoilSampleId === item.nativeSoilSampleId
        ) {
          rowSpanCount++;
        } else {
          break;
        }
      }

      const row: any[] = [
        '',
        rowSpanCount > 1
          ? {
              content: item.sampleId || '',
              rowSpan: rowSpanCount,
              styles: {
                valign: 'middle',
                halign: 'center'
              }
            }
          : item.sampleId || '',

        item.effort || '',
        depthRange,

        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        rowSpanCount > 1
          ? {
              content: item.clientSpecifiedMoistureContent,
              rowSpan: rowSpanCount,
              styles: {
                valign: 'middle',
                halign: 'center'
              }
            }
          : item.clientSpecifiedMoistureContent,

        item.clientSpecifiedDensity,

        rowSpanCount > 1
          ? {
              content: item.clientDescription,
              rowSpan: rowSpanCount,
              styles: {
                valign: 'middle',
                halign: 'center'
              }
            }
          : item.clientDescription,

        rowSpanCount > 1
          ? {
              content: '',
              rowSpan: rowSpanCount,
              styles: {
                valign: 'middle',
                halign: 'center'
              }
            }
          : '',
      ];

      worksheetRows.push(row);

      // DUPLICATE ROWS
      for (let k = 1; k < rowSpanCount; k++) {
        const nextItem = worksheets[i + k];
        const nextDepthRange =
          hasValue(nextItem.depth) &&
          hasValue(nextItem.endDepth)
            ? `${nextItem.depth}-${nextItem.endDepth}`
            : hasValue(nextItem.depth)
              ? nextItem.depth
              : hasValue(nextItem.endDepth)
                ? nextItem.endDepth
                : '';

        worksheetRows.push([
          '',
          nextItem.effort || '',
          nextDepthRange,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          nextItem.clientSpecifiedDensity,
          '',
          '',
          '',
        ]);
      }

      i += rowSpanCount - 1;
    }

    // MAIN TABLE
    autoTable(doc, {
      startY: finalY,
      theme: 'grid',
      margin: {
        left: margin,
        right: margin,
        top: finalY
      },

      tableWidth: usableWidth,

      // DRAW HEADER ON EVERY PAGE
      didDrawPage: () => {
        this.drawHeader(
          doc,
          worksheetRecords,
          margin,
          usableWidth
        );
      },

      head: [
        [
          {
            content: 'In\nHouse\nID',
            rowSpan: 2
          },
          {
            content: 'Sample\nID',
            rowSpan: 2
          },
          {
            content: 'Comp.\nEffort\n(%)',
            rowSpan: 2
          },
          {
            content: 'Depth\n(ft)',
            rowSpan: 2
          },
          {
            content: 'M+P Tare\n(gm)',
            rowSpan: 2
          },
          {
            content: 'Test 1',
            colSpan: 2,
            styles: { halign: 'center' }
          },
          {
            content: 'Test 2',
            colSpan: 2,
            styles: { halign: 'center' }
          },
          {
            content: 'Test 3',
            colSpan: 2,
            styles: { halign: 'center' }
          },
          {
            content: 'Test 4',
            colSpan: 2,
            styles: { halign: 'center' }
          },
          {
            content: 'Dry Test',
            colSpan: 2,
            styles: { halign: 'center' }
          },
          {
            content: 'Client\nMC\n(%)',
            rowSpan: 2
          },
          {
            content: 'Client\nDensity\n(pcf)',
            rowSpan: 2
          },
          {
            content: 'Sample\nDesc.',
            rowSpan: 2
          },
          {
            content: 'Geotherm\ndesc.',
            rowSpan: 2
          },
        ],

        [
          'Weight',
          'TR',
          'Weight',
          'TR',
          'Weight',
          'TR',
          'Weight',
          'TR',
          'Dry Weight',
          'Dry TR'
        ]
      ],

      didParseCell: (data) => {
        if (data.section !== 'body') {
            return;
        }

        const currentIndex = data.row.index;
        const currentItem = worksheets[currentIndex];
        const nextItem = worksheets[currentIndex + 1];
        const isLastMergedRow = !nextItem || currentItem.nativeSoilSampleId !== nextItem.nativeSoilSampleId;

        // NORMAL ROW CELLS
        if (isLastMergedRow) {

            Object.values(data.row.cells).forEach((cell: any) => {
              cell.styles.lineWidth = {
                  top: 0.1,
                  right: 0.1,
                  bottom: 0.5,
                  left: 0.1
              };
            });
        }

        // ROWSPAN CELLS
        if (data.cell.rowSpan && data.cell.rowSpan > 1) {
            data.cell.styles.lineWidth = {
              top: 0.1,
              right: 0.1,
              bottom: 0.5,
              left: 0.1
            };
        }
      },

      body: worksheetRows,

      styles: {
        fontSize: 8,
        textColor: 0,
        lineColor: 0,
        lineWidth: 0.1,
        cellPadding: 1,
        minCellHeight: 9,
        valign: 'middle',
        halign: 'center',
      },

      headStyles: {
        fillColor: [220, 220, 220],
        textColor: 0,
        fontStyle: 'bold',
        valign: 'middle',
        halign: 'center'
      },

      columnStyles: {

        0: { cellWidth: usableWidth / 100 * 4 },
        1: { cellWidth: usableWidth / 100 * 5 },
        2: { cellWidth: usableWidth / 100 * 4 },
        3: { cellWidth: usableWidth / 100 * 4 },
        4: { cellWidth: usableWidth / 100 * 5 },

        // TEST #1
        5: { cellWidth: usableWidth / 100 * 6 },
        6: { cellWidth: usableWidth / 100 * 5 },

        // TEST #2
        7: { cellWidth: usableWidth / 100 * 6 },
        8: { cellWidth: usableWidth / 100 * 5 },

        // TEST #3
        9: { cellWidth: usableWidth / 100 * 6 },
        10: { cellWidth: usableWidth / 100 * 5 },

        // TEST #4
        11: { cellWidth: usableWidth / 100 * 6 },
        12: { cellWidth: usableWidth / 100 * 5 },

        // DRY TEST
        13: { cellWidth: usableWidth / 100 * 6 },
        14: { cellWidth: usableWidth / 100 * 5 },

        15: { cellWidth: usableWidth / 100 * 5 },
        16: { cellWidth: usableWidth / 100 * 5 },
        17: { cellWidth: usableWidth / 100 * 6 },
        18: { cellWidth: usableWidth / 100 * 6 }
      }
    });
  }
}